import { FC, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button, NumberInput, Preloader } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from 'auth/constants';
import { CurrentClubCard } from 'auth/CurrentClubCard';
import { GET_ME, START_WORK_SHIFT } from 'auth/schema';
import { IStartWorkShiftResponse } from 'auth/types';
import { CashboxContext, useIsCashboxEnabled } from 'cashbox';
import { FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { getRandomNumber } from 'utils';

import { IFormWorkShiftValues, IOpenWorkShiftProps } from '../interfaces';
import s from './OpenWorkShift.module.scss';

export const OpenWorkShift: FC<IOpenWorkShiftProps> = ({
  returnToClubSelect,
  selectedClub,
  token,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const isCashboxEnabled = useIsCashboxEnabled();

  const navigate = useNavigate();

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { openWorkShift: openCashboxWorkShift } = useContext(CashboxContext);
  const { loadFlags } = useContext(FeaturesContext);

  const [isFeaturesLoading, setIsFeaturesLoading] = useState(false);

  const [startWorkShift] = useMutation<IStartWorkShiftResponse>(
    START_WORK_SHIFT,
    {
      refetchQueries: [GET_ME],
      awaitRefetchQueries: true,
      onCompleted: () => navigate('/', { replace: true }),
      onError: (error) => {
        notification.info({
          t,
          type: 'error',
          title: t('errors.error'),
          message: error?.message.includes('work shift')
            ? t('workShifts.error.title')
            : undefined,
        });
      },
    }
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
  } = useForm<IFormWorkShiftValues>({ mode: 'onChange' });

  useEffect(() => {
    if (!token) return;

    setIsFeaturesLoading(true);

    loadFlags(token).finally(() => setIsFeaturesLoading(false));
  }, []);

  return (
    <Preloader loading={isFeaturesLoading && !permissions}>
      <form
        className={s.container}
        onSubmit={handleSubmit(({ cash }) => {
          const variables = { input: { cash } };

          return new Promise((resolve) => {
            if (isCashboxEnabled) {
              return openCashboxWorkShift()
                .then((data) => resolve(data))
                .catch(() => reset());
            }

            resolve(null);
          }).then(() =>
            startWorkShift({
              variables,
              context: {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            })
          );
        })}
      >
        <header className={s.header}>{t('workShifts.open.title')}</header>
        <div className={s.content}>
          <Controller
            render={({ field: { ...fieldProps }, fieldState: { error } }) => (
              <NumberInput
                {...fieldProps}
                title={t('money.inCashBox')}
                placeholder={t('sum')}
                error={error?.message}
                bordered
                name={`cash-${getRandomNumber()}`}
              />
            )}
            name="cash"
            control={control}
            defaultValue=""
            rules={{
              required: t('errors.required'),
              min: {
                value: 0,
                message: t('errors.min', 0, currency.sign),
              },
              max: {
                value: 16000000,
                message: t('errors.max', '16 000 000', currency.sign),
              },
            }}
          />
          <CurrentClubCard club={selectedClub} />
        </div>
        <div className={s.controls}>
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={!isDirty}
            fullWidth
          >
            {t('workShifts.open.submit')}
          </Button>
          {permissions?.has('can_login_into_work_shift') && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/', { replace: true })}
              className={s['select-club']}
            >
              {t('workShifts.skip')}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => returnToClubSelect()}
            className={s['select-club']}
          >
            {t('clubs.change')}
          </Button>
        </div>
      </form>
    </Preloader>
  );
};
